/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: robopenpaye.tsx
 *
 *  author: tcleret
 *
 *  creation: 25/11/2025 - 10:00
 *
 *  description:
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */

import * as React from "react";
import { apiClient } from "../utils/utils.tsx";
import DataTable from "react-data-table-component";

/**
 * Interface for the employee search in Migrapaye
 */
export default function Robopenpaye({ domainsList }) {
   const [config, setConfig] = React.useState([]);
   const [directories, setDirectories] = React.useState([]);
   const [selected, setSelected] = React.useState([]);
   const [domainsSelect, setDomainsSelect] = React.useState([]);
   const searchDom = "searchDom";
   const domainDisplayAll = "Afficher tous les domaines";
   const ID_activated = 0;
   const ID_domain = 1;
   const ID_dirId = 2;
   const ID_dirName = 4;
   const ID_responsible = 5;

   // Table display configuration
   const columns_cfg = [
      {
         name: "Activé",
         selector: (row) => (row[ID_activated] === "1" ? "Oui" : "Non"),
         sortable: true,
         width: "120px",
         grow: 0,
      },
      { name: "Domaine", selector: (row) => row[ID_domain], sortable: true, width: "200px", grow: 0 },
      { name: "ID", selector: (row) => row[ID_dirId], sortable: true, width: "150px", grow: 0 },
      { name: "Nom dossier", selector: (row) => row[ID_dirName], sortable: true, grow: 0.5 },
      { name: "Responsable", selector: (row) => row[ID_responsible], sortable: true, grow: 0.5 },
   ];
   const pagination_cfg = {
      rowsPerPageText: "Dossiers par page",
      rangeSeparatorText: "sur",
      selectAllRowsItem: true,
      selectAllRowsItemText: "Tous",
   };

   // Callbacks for the datatables to keep track of the selected rows
   const handleSelected = React.useCallback(
      (state) => {
         setSelected(state.selectedRows);
      },
      [selected]
   );

   //  Handler for the action menus
   const contextActions = React.useMemo(() => {
      return displayActionButtons();
   }, [config, directories, selected]);

   // --------------------------------------------------------------------------
   //  Functions section
   // --------------------------------------------------------------------------

   /**
    * Filter displayed directories using the dropdown
    *
    * @param {list} cfg the configuration to use for the filtering. State is used if null
    */
   function filterByDomain(cfg) {
      const dirList = [];

      let selectedDomain = domainDisplayAll;
      if (document.getElementById(searchDom) != null) {
         selectedDomain = document.getElementById(searchDom).value;
      }

      if (cfg == null) {
         cfg = config;
      }
      for (const dir of cfg) {
         if (
            dir[ID_domain].replace(" ", "_") == selectedDomain.replace(" ", "_") ||
            selectedDomain == domainDisplayAll
         ) {
            dirList.push(dir);
         }
      }
      setDirectories(dirList);
   }

   /**
    * Activate/deactivate the selected directory
    */
   async function activateDirectory() {
      await apiClient.post("/robopenpaye/setActivated", {
         activated: [[selected[0][ID_activated] === "0", selected[0][ID_dirId]]],
      });
      readConfig();
      setSelected([]);
   }

   /**
    * Display logic for the action menus
    */
   function displayActionButtons() {
      const tableTitle = "Activation des différents dossiers par domaine";
      let className = "table-button-disabled";
      let title = "Activation";
      if (selected.length != 0) {
         className = "table-button-add";
         title = "Activer";
         if (selected[0][ID_activated] === "1") {
            className = "table-button-modify";
            title = "Désactiver";
         }
      }

      return (
         <div className="table-header">
            <div className="table-title">{tableTitle}</div>
            <select
               id={searchDom}
               className="dropdown"
               defaultValue={domainDisplayAll}
               onChange={() => filterByDomain(null)}
            >
               {domainsSelect}
            </select>
            <div
               className="table-button-div"
               style={{
                  marginTop: "10px",
                  marginBottom: "10px",
               }}
            >
               <button className={className} disabled={selected.length != 0} key="activate" onClick={activateDirectory}>
                  {title}
               </button>
            </div>
         </div>
      );
   }

   /**
    * Read the configuration from the backend, handle display
    */
   async function readConfig() {
      const reply = await apiClient.get("/robopenpaye/getConfig");
      const cfg = reply.data.config;
      setConfig(cfg);
      filterByDomain(cfg);
   }

   // --------------------------------------------------------------------------
   //  Logic section
   // --------------------------------------------------------------------------

   // Populate the domains selector
   if (domainsSelect.length < 1) {
      const _domainsSelect = [];
      const i = 0;
      _domainsSelect.push(
         <option key={0} value={domainDisplayAll} selected={true}>
            {domainDisplayAll}
         </option>
      );
      for (let i = 0; i < domainsList.length; i++) {
         _domainsSelect.push(
            <option key={i + 1} value={domainsList[i]} selected={false}>
               {domainsList[i]}
            </option>
         );
      }
      setDomainsSelect(_domainsSelect);
   }

   // Read the config at page loading
   if (config.length < 1) {
      readConfig();
   }

   return (
      <div className="action_content">
         <DataTable
            columns={columns_cfg}
            data={directories}
            highlightOnHover
            striped
            dense
            subHeader
            subHeaderComponent={contextActions}
            noDataComponent={<div> Pas de dossiers pour le domaine sélectionné </div>}
            progressPending={config.length == 0}
            progressComponent={<div> Chargement de la configuration ... </div>}
            pagination
            paginationComponentOptions={pagination_cfg}
            selectableRows
            selectableRowsSingle
            selectableRowsHighlight
            onSelectedRowsChange={handleSelected}
         />
      </div>
   );
}
